import React, { useEffect, useState } from 'react';
import { Box, Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material';

// Missed-tour rebooking offers (backend demo.lib.missed_tour_rebooking, config
// ai_missed_tour_rebooking). Suggestion-only by design: there is no auto-send option. A guest who missed their tour is offered the next slots
// for this fee; their choice and email become a request card in the chat for staff
// to approve. Shown only once the platform team has made the feature available for
// the operator (the config key exists); the whole object is saved at once because
// the dashboard's config merge is shallow.

export const toConfig = (form) => ({
  enabled: !!form.enabled,
  fee_per_person: Number(form.fee_per_person),
  currency: String(form.currency || 'EUR').trim().toUpperCase(),
  rebook_within_days: parseInt(form.rebook_within_days, 10),
  allow_ota_bookings: !!form.allow_ota_bookings,
});

export const formErrors = (form) => {
  const errors = {};
  const fee = Number(form.fee_per_person);
  if (form.fee_per_person === '' || !Number.isFinite(fee) || fee < 0) errors.fee_per_person = 'Enter the fee per person';
  if (!/^[A-Za-z]{3}$/.test(String(form.currency || '').trim())) errors.currency = 'Three-letter code, e.g. EUR';
  const days = parseInt(form.rebook_within_days, 10);
  if (!Number.isInteger(days) || days < 1 || days > 30) errors.rebook_within_days = '1 to 30 days';
  return errors;
};

const fromConfig = (value) => ({
  enabled: !!value?.enabled,
  fee_per_person: value?.fee_per_person ?? '',
  currency: value?.currency || 'EUR',
  rebook_within_days: value?.rebook_within_days ?? 3,
  allow_ota_bookings: value?.allow_ota_bookings ?? true,
});

export default function MissedTourRebookingSetting({ value, disabled, onSave }) {
  const [form, setForm] = useState(fromConfig(value));
  useEffect(() => { setForm(fromConfig(value)); }, [value]);
  const errors = formErrors(form);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const dirty = JSON.stringify(toConfig(form)) !== JSON.stringify(toConfig(fromConfig(value)));

  return (
    <Box data-testid="missed-tour-rebooking-setting">
      <Typography variant="h6" sx={{ mb: 0.5 }}>Missed-tour rebooking</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 640 }}>
        When a guest tells us they missed their tour, the AI offers them another time for your rebooking fee.
        Once they choose a time and confirm their email, a request appears in the chat for your team to approve
        and send the payment link. Nothing is charged or changed in your booking system automatically.
      </Typography>
      <FormControlLabel
        control={<Switch checked={form.enabled} onChange={set('enabled')} disabled={disabled} />}
        label="Offer rebooking to guests who missed their tour"
      />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5 }}>
        <TextField size="small" label="Fee per person" value={form.fee_per_person} onChange={set('fee_per_person')}
                   disabled={disabled} error={!!errors.fee_per_person} helperText={errors.fee_per_person || ' '}
                   inputProps={{ inputMode: 'decimal', 'data-testid': 'rebooking-fee-input' }} sx={{ width: 150 }} />
        <TextField size="small" label="Currency" value={form.currency} onChange={set('currency')}
                   disabled={disabled} error={!!errors.currency} helperText={errors.currency || ' '}
                   inputProps={{ maxLength: 3 }} sx={{ width: 110 }} />
        <TextField size="small" label="Offer times within (days)" value={form.rebook_within_days}
                   onChange={set('rebook_within_days')} disabled={disabled} error={!!errors.rebook_within_days}
                   helperText={errors.rebook_within_days || ' '} inputProps={{ inputMode: 'numeric' }} sx={{ width: 190 }} />
      </Box>
      <FormControlLabel
        control={<Switch checked={form.allow_ota_bookings} onChange={set('allow_ota_bookings')} disabled={disabled} />}
        label="Include guests who booked through an OTA (Viator, GetYourGuide, ...)"
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, maxWidth: 640 }}>
        These messages are always suggestions for your team to review and send, never sent automatically,
        so a guest is never quoted a fee without a person checking first. They need suggested replies to be
        switched on above.
      </Typography>
      <Box sx={{ mt: 1.5 }}>
        <Button variant="contained" size="small" disabled={disabled || !dirty || Object.keys(errors).length > 0}
                onClick={() => onSave(toConfig(form))}>
          Save
        </Button>
      </Box>
    </Box>
  );
}
